import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Trash2, Plus, Users, Baby, Sparkles, AlertCircle, CheckCircle2, TrendingUp } from "lucide-react";

const euro = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function ResultCard({ title, items }) {
  return (
    <Card className="p-6 rounded-2xl border bg-accent/40" data-testid="calc-result">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">{title}</div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.label} className="flex justify-between items-baseline">
            <span className="text-sm text-muted-foreground">{it.label}</span>
            <span className={`font-heading font-semibold ${it.highlight ? "text-2xl text-primary" : "text-base"}`}>{it.value}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function IrpefCalc() {
  const [reddito, setReddito] = useState(30000);
  // Fix R3: default 0 → il backend applica art.13 TUIR dinamica come il widget busta paga
  const [detrazioni, setDetrazioni] = useState(0);
  const [r, setR] = useState(null);
  const submit = async () => {
    try { const res = await api.post("/calc/irpef", { reddito_lordo: Number(reddito), detrazioni: Number(detrazioni) }); setR(res.data); }
    catch { toast.error("Errore calcolo"); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-6 rounded-2xl border">
        <h3 className="font-heading font-semibold text-xl mb-4">IRPEF (dipendenti/pensionati)</h3>
        <div className="space-y-4">
          <div><Label>Reddito lordo annuo (€)</Label><Input type="number" value={reddito} onChange={(e) => setReddito(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="irpef-reddito" /></div>
          <div>
            <Label>Detrazioni extra (€)</Label>
            <Input type="number" value={detrazioni} onChange={(e) => setDetrazioni(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="irpef-detrazioni" placeholder="0" />
            <p className="text-[11px] text-muted-foreground mt-1">
              Lascia 0: la detrazione da lavoro dipendente (art.13 TUIR) è calcolata automaticamente.
            </p>
          </div>
          <Button onClick={submit} className="rounded-full w-full h-12" data-testid="irpef-submit">Calcola IRPEF</Button>
        </div>
      </Card>
      {r && <>
        <ResultCard title="Risultato IRPEF" items={[
          { label: "Imposta lorda", value: euro(r.imposta_lorda) },
          { label: `Detrazioni ${r.detrazione_source ? '('+r.detrazione_source+')' : ''}`, value: `- ${euro(r.detrazioni)}` },
          { label: "Imposta netta", value: euro(r.imposta_netta), highlight: true },
          { label: "Netto annuo (solo IRPEF)", value: euro(r.netto_annuo) },
          { label: "Netto mensile (x13)", value: euro(r.netto_mensile) },
          { label: "Aliquota effettiva", value: `${r.aliquota_effettiva}%` },
        ]} />
        <p className="md:col-span-2 text-xs text-muted-foreground -mt-3">
          Nota: il "Netto" qui è solo al netto dell'IRPEF. Per il netto reale in busta paga (comprensivo di contributi INPS 9,19% e addizionali regionale/comunale) usa il calcolatore Busta Paga.
        </p>
      </>}
    </div>
  );
}

function ForfettarioCalc() {
  const [ricavi, setRicavi] = useState(50000);
  const [coeff, setCoeff] = useState(0.78);
  const [startup, setStartup] = useState(false);
  const [inps, setInps] = useState(4200);
  const [r, setR] = useState(null);
  const submit = async () => {
    try { const res = await api.post("/calc/forfettario", { ricavi: Number(ricavi), coefficiente: Number(coeff), is_startup: startup, contributi_inps: Number(inps) }); setR(res.data); }
    catch { toast.error("Errore calcolo"); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-6 rounded-2xl border">
        <h3 className="font-heading font-semibold text-xl mb-4">Regime Forfettario</h3>
        <div className="space-y-4">
          <div><Label>Ricavi/compensi (€)</Label><Input type="number" value={ricavi} onChange={(e) => setRicavi(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="forf-ricavi" /></div>
          <div><Label>Coefficiente redditività (0-1)</Label><Input type="number" step="0.01" value={coeff} onChange={(e) => setCoeff(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="forf-coeff" /></div>
          <div><Label>Contributi INPS versati (€)</Label><Input type="number" value={inps} onChange={(e) => setInps(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="forf-inps" /></div>
          <div className="flex items-center justify-between rounded-xl border p-4">
            <div><Label className="font-medium">Startup (primi 5 anni, aliquota 5%)</Label></div>
            <Switch checked={startup} onCheckedChange={setStartup} data-testid="forf-startup" />
          </div>
          <Button onClick={submit} className="rounded-full w-full h-12" data-testid="forf-submit">Calcola</Button>
        </div>
      </Card>
      {r && <ResultCard title="Risultato Forfettario" items={[
        { label: "Reddito imponibile", value: euro(r.reddito_imponibile) },
        { label: "Contributi dedotti", value: `- ${euro(r.contributi_inps)}` },
        { label: "Base tassabile", value: euro(r.reddito_tassabile) },
        { label: `Imposta ${r.aliquota}%`, value: euro(r.imposta_sostitutiva), highlight: true },
        { label: "Netto stimato", value: euro(r.netto_stimato) },
      ]} />}
    </div>
  );
}

function IvaCalc() {
  const [imp, setImp] = useState(1000);
  const [aliq, setAliq] = useState(22);
  const [r, setR] = useState(null);
  const submit = async () => { try { const res = await api.post("/calc/iva", { imponibile: Number(imp), aliquota: Number(aliq) }); setR(res.data); } catch { toast.error("Errore"); } };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-6 rounded-2xl border">
        <h3 className="font-heading font-semibold text-xl mb-4">Calcolo IVA</h3>
        <div className="space-y-4">
          <div><Label>Imponibile (€)</Label><Input type="number" value={imp} onChange={(e) => setImp(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="iva-imponibile" /></div>
          <div><Label>Aliquota (%)</Label>
            <Select value={String(aliq)} onValueChange={(v) => setAliq(v)}>
              <SelectTrigger className="h-12 rounded-xl mt-1.5" data-testid="iva-aliquota"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="4">4% (agevolata)</SelectItem>
                <SelectItem value="5">5% (ridotta)</SelectItem>
                <SelectItem value="10">10% (ridotta)</SelectItem>
                <SelectItem value="22">22% (ordinaria)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={submit} className="rounded-full w-full h-12" data-testid="iva-submit">Calcola IVA</Button>
        </div>
      </Card>
      {r && <ResultCard title="Risultato IVA" items={[
        { label: "Imponibile", value: euro(r.imponibile) },
        { label: `IVA ${r.aliquota}%`, value: euro(r.iva) },
        { label: "Totale", value: euro(r.totale), highlight: true },
      ]} />}
    </div>
  );
}

function IseeCalc() {
  const [redditi, setRedditi] = useState(25000);
  const [mob, setMob] = useState(5000);
  const [imm, setImm] = useState(0);
  const [mutuo, setMutuo] = useState(0);
  const [primaCasa, setPrimaCasa] = useState(false);
  const [canone, setCanone] = useState(0);
  const [nucleo, setNucleo] = useState(3);
  const [minori, setMinori] = useState(1);
  const [disabili, setDisabili] = useState(0);
  const [monoGenitore, setMonoGenitore] = useState(false);
  const [entrambiLavorano, setEntrambiLavorano] = useState(false);
  const [figli3plus, setFigli3plus] = useState(false);
  const [r, setR] = useState(null);
  const submit = async () => {
    try {
      const res = await api.post("/calc/isee", {
        redditi_totali: Number(redditi),
        patrimonio_mobiliare: Number(mob),
        patrimonio_immobiliare: Number(imm),
        mutuo_residuo: Number(mutuo),
        prima_casa: primaCasa,
        canone_locazione: Number(canone),
        componenti_nucleo: Number(nucleo),
        minori: Number(minori),
        figli_disabili: Number(disabili),
        genitore_solo_minori: monoGenitore,
        entrambi_lavorano_con_minori_3anni: entrambiLavorano,
        figli_minori_3plus: figli3plus,
      });
      setR(res.data);
    } catch { toast.error("Errore"); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-6 rounded-2xl border">
        <h3 className="font-heading font-semibold text-xl mb-1">ISEE avanzato</h3>
        <p className="text-xs text-muted-foreground mb-4">Formula INPS 2024 con maggiorazioni scala equivalenza.</p>
        <div className="space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-secondary mb-2">Redditi</div>
            <Label>Redditi totali nucleo (€)</Label>
            <Input type="number" value={redditi} onChange={(e) => setRedditi(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="isee-redditi" />
          </div>
          <div>
            <Label>Canone locazione annuo (€, se in affitto)</Label>
            <Input type="number" value={canone} onChange={(e) => setCanone(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="isee-canone" />
            <p className="text-xs text-muted-foreground mt-1">Deducibile fino a €7.000/anno.</p>
          </div>

          <div className="pt-2 border-t">
            <div className="text-xs font-bold uppercase tracking-widest text-secondary mb-2">Patrimonio</div>
            <Label>Patrimonio mobiliare (€) — depositi, titoli, azioni</Label>
            <Input type="number" value={mob} onChange={(e) => setMob(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="isee-mob" />
          </div>
          <div>
            <Label>Patrimonio immobiliare (€) — valore IMU</Label>
            <Input type="number" value={imm} onChange={(e) => setImm(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="isee-imm" />
          </div>
          <div>
            <Label>Mutuo residuo prima casa (€)</Label>
            <Input type="number" value={mutuo} onChange={(e) => setMutuo(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="isee-mutuo" />
          </div>
          <div className="flex items-center justify-between rounded-xl border p-4">
            <div>
              <Label className="font-medium">Immobile è prima casa</Label>
              <p className="text-xs text-muted-foreground">Detrazione €52.500 dal valore</p>
            </div>
            <Switch checked={primaCasa} onCheckedChange={setPrimaCasa} data-testid="isee-primacasa" />
          </div>

          <div className="pt-2 border-t">
            <div className="text-xs font-bold uppercase tracking-widest text-secondary mb-2">Nucleo familiare</div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Componenti</Label><Input type="number" min="1" value={nucleo} onChange={(e) => setNucleo(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="isee-nucleo" /></div>
              <div><Label>Di cui minori</Label><Input type="number" min="0" value={minori} onChange={(e) => setMinori(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="isee-minori" /></div>
              <div><Label>Disabili</Label><Input type="number" min="0" value={disabili} onChange={(e) => setDisabili(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="isee-disabili" /></div>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border p-3">
            <Label className="font-medium text-sm">Genitore solo con figli minori (+0.2)</Label>
            <Switch checked={monoGenitore} onCheckedChange={setMonoGenitore} data-testid="isee-monogen" />
          </div>
          <div className="flex items-center justify-between rounded-xl border p-3">
            <Label className="font-medium text-sm">3+ figli minori (+0.35)</Label>
            <Switch checked={figli3plus} onCheckedChange={setFigli3plus} data-testid="isee-3plus" />
          </div>
          <div className="flex items-center justify-between rounded-xl border p-3">
            <Label className="font-medium text-sm">Entrambi lavorano, figli sotto 3 anni (+0.2)</Label>
            <Switch checked={entrambiLavorano} onCheckedChange={setEntrambiLavorano} data-testid="isee-entrambi" />
          </div>

          <Button onClick={submit} className="rounded-full w-full h-12" data-testid="isee-submit">Calcola ISEE</Button>
        </div>
      </Card>
      {r && (
        <div className="space-y-4">
          <ResultCard title="Risultato ISEE" items={[
            { label: "ISEE stimato", value: euro(r.isee), highlight: true },
            { label: "Fascia", value: r.fascia },
            { label: "ISR (Reddito)", value: euro(r.isr) },
            { label: "ISP (Patrimonio)", value: euro(r.isp) },
            { label: "Franchigia mobiliare applicata", value: `- ${euro(r.franchigia_mobiliare_applicata)}` },
            { label: "Valore immobile netto", value: euro(r.valore_immobiliare_netto) },
            { label: "Scala equivalenza", value: r.scala_equivalenza },
          ]} />
          {r.maggiorazioni && r.maggiorazioni.length > 0 && (
            <Card className="p-4 rounded-2xl border bg-primary/5" data-testid="isee-maggiorazioni">
              <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Maggiorazioni applicate</div>
              <ul className="text-sm space-y-1">
                {r.maggiorazioni.map((m, i) => <li key={`m-${m}`}>✓ {m}</li>)}
              </ul>
            </Card>
          )}
          <Card className="p-4 rounded-2xl border bg-accent/40" data-testid="isee-note">
            <ul className="text-xs text-muted-foreground space-y-1">
              {r.note.map((n, i) => <li key={`n-${i}-${String(n).slice(0,30)}`}>• {n}</li>)}
            </ul>
          </Card>
        </div>
      )}
    </div>
  );
}

function BolloCalc() {
  const [kw, setKw] = useState(85);
  const [classe, setClasse] = useState("euro6");
  const [r, setR] = useState(null);
  const submit = async () => { try { const res = await api.post("/calc/bollo", { kw: Number(kw), classe_ambientale: classe }); setR(res.data); } catch { toast.error("Errore"); } };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-6 rounded-2xl border">
        <h3 className="font-heading font-semibold text-xl mb-4">Bollo Auto</h3>
        <div className="space-y-4">
          <div><Label>Potenza (kW)</Label><Input type="number" value={kw} onChange={(e) => setKw(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="bollo-kw" /></div>
          <div><Label>Classe ambientale</Label>
            <Select value={classe} onValueChange={setClasse}>
              <SelectTrigger className="h-12 rounded-xl mt-1.5" data-testid="bollo-classe"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["euro0","euro1","euro2","euro3","euro4","euro5","euro6"].map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={submit} className="rounded-full w-full h-12" data-testid="bollo-submit">Calcola bollo</Button>
        </div>
      </Card>
      {r && <ResultCard title="Bollo annuo" items={[
        { label: "Tariffa base", value: `€ ${r.tariffa_base}/kW` },
        { label: "Maggiorazione classe", value: `x ${r.maggiorazione}` },
        { label: "Bollo annuo", value: euro(r.bollo_annuo), highlight: true },
      ]} />}
    </div>
  );
}

// ============================================================================
// CALCOLATORI DIPENDENTI (busta paga, AUU, bonus mamma, bonus asilo)
// ============================================================================

function BustaPagaCalc({ user }) {
  const [ral, setRal] = useState(user?.ral_lordo || 30000);
  const [mensilita, setMensilita] = useState(user?.mensilita || 13);
  const [tipoContratto, setTipoContratto] = useState(user?.tipo_contratto || "indeterminato");
  const [giorni, setGiorni] = useState(365);
  const [altreDetr, setAltreDetr] = useState(0);
  const [r, setR] = useState(null);
  const submit = async () => {
    try {
      const res = await api.post("/calc/busta-paga", {
        ral: Number(ral), mensilita: Number(mensilita), tipo_contratto: tipoContratto,
        giorni_lavorati: Number(giorni), altre_detrazioni: Number(altreDetr),
        aliquota_add_regionale: user?.aliquota_add_regionale ?? null,
        aliquota_add_comunale: user?.aliquota_add_comunale ?? null,
      });
      setR(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore calcolo"); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-6 rounded-2xl border">
        <h3 className="font-heading font-semibold text-xl mb-1">Netto in busta paga</h3>
        <p className="text-xs text-muted-foreground mb-4">Calcolo trasparente con detrazioni art.13 TUIR, Trattamento Integrativo e addizionali.</p>
        <div className="space-y-4">
          <div><Label>RAL — Reddito Annuo Lordo (€)</Label><Input type="number" value={ral} onChange={(e) => setRal(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="bp-ral" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Mensilità</Label>
              <Select value={String(mensilita)} onValueChange={(v) => setMensilita(v)}>
                <SelectTrigger className="h-12 rounded-xl mt-1.5" data-testid="bp-mens"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="12">12</SelectItem><SelectItem value="13">13</SelectItem><SelectItem value="14">14</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Tipo contratto</Label>
              <Select value={tipoContratto} onValueChange={setTipoContratto}>
                <SelectTrigger className="h-12 rounded-xl mt-1.5" data-testid="bp-contratto"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="indeterminato">Indeterminato</SelectItem><SelectItem value="determinato">Determinato</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Giorni lavorati (365 = anno pieno)</Label><Input type="number" value={giorni} onChange={(e) => setGiorni(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="bp-giorni" /></div>
            <div><Label>Altre detrazioni (€)</Label><Input type="number" value={altreDetr} onChange={(e) => setAltreDetr(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="bp-altre" /></div>
          </div>
          <Button onClick={submit} className="rounded-full w-full h-12" data-testid="bp-submit">Calcola netto</Button>
        </div>
      </Card>
      {r && (
        <Card className="p-6 rounded-2xl border bg-accent/40" data-testid="bp-result">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">Il tuo netto</div>
          <div className="mb-4">
            <div className="text-3xl font-heading font-bold text-primary">{euro(r.netto_annuo)}</div>
            <div className="text-sm text-muted-foreground">annuo · {euro(r.netto_mensile)} × {r.mensilita} mensilità</div>
          </div>
          <div className="space-y-2 text-sm border-t pt-3">
            <Row label={`RAL`} value={euro(r.ral)} />
            <Row label={`− INPS dipendente (${r.inps_aliquota_pct}%)`} value={`- ${euro(r.inps_dipendente)}`} negative />
            <Row label="= Imponibile IRPEF" value={euro(r.reddito_imponibile)} bold />
            <Row label="IRPEF lorda (scaglioni)" value={`- ${euro(r.irpef_lorda)}`} negative />
            <Row label={`+ Detrazione art.13 (${r.detrazione_lavoro.formula})`} value={`+ ${euro(r.detrazione_lavoro.importo)}`} positive />
            {r.altre_detrazioni > 0 && <Row label="+ Altre detrazioni" value={`+ ${euro(r.altre_detrazioni)}`} positive />}
            <Row label="= IRPEF netta" value={`- ${euro(r.irpef_netta)}`} negative bold />
            {r.trattamento_integrativo.importo > 0 && (
              <Row label={`+ Trattamento Integrativo (${r.trattamento_integrativo.motivo})`} value={`+ ${euro(r.trattamento_integrativo.importo)}`} positive />
            )}
            <Row label={`− Addizionale regionale (${r.aliquota_add_regionale_pct}%)`} value={`- ${euro(r.addizionale_regionale)}`} negative />
            <Row label={`− Addizionale comunale (${r.aliquota_add_comunale_pct}%)`} value={`- ${euro(r.addizionale_comunale)}`} negative />
          </div>
          <ul className="mt-4 text-xs text-muted-foreground space-y-1 border-t pt-3">
            {r.note.map((n, i) => <li key={`n-${i}-${String(n).slice(0,30)}`}>• {n}</li>)}
          </ul>
        </Card>
      )}
    </div>
  );
}

function Row({ label, value, bold, positive, negative }) {
  return (
    <div className="flex justify-between items-baseline gap-3">
      <span className={`text-xs ${bold ? "font-semibold text-foreground" : "text-muted-foreground"}`}>{label}</span>
      <span className={`font-mono text-sm ${bold ? "font-bold" : ""} ${positive ? "text-primary" : negative ? "text-foreground" : ""}`}>{value}</span>
    </div>
  );
}

function AssegnoUnicoCalc({ user }) {
  // Stable _key per figlio (evita perdita focus/state quando si rimuove un item in mezzo)
  const [figli, setFigli] = useState(() => {
    const initial = user?.figli_a_carico?.length ? user.figli_a_carico : [{ eta: 5, disabilita: null }];
    return initial.map((f, i) => ({ ...f, _key: f._key || `f-init-${i}` }));
  });
  const [isee, setIsee] = useState(user?.isee || "");
  const [madreU21, setMadreU21] = useState(user?.madre_under_21 || false);
  const [entrambiLav, setEntrambiLav] = useState(user?.entrambi_genitori_lavoratori || false);
  const [r, setR] = useState(null);

  const addFiglio = () => setFigli([...figli, { eta: 0, disabilita: null, _key: `f-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` }]);
  const removeFiglio = (i) => setFigli(figli.filter((_, idx) => idx !== i));
  const updateFiglio = (i, field, val) => setFigli(figli.map((f, idx) => idx === i ? { ...f, [field]: val } : f));

  const submit = async () => {
    try {
      const res = await api.post("/calc/assegno-unico", {
        figli: figli.map((f) => ({ eta: Number(f.eta), disabilita: f.disabilita || null })),
        isee: isee ? Number(isee) : null,
        madre_under_21: madreU21,
        entrambi_genitori_lavoratori: entrambiLav,
      });
      setR(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore calcolo"); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-6 rounded-2xl border">
        <h3 className="font-heading font-semibold text-xl mb-1">Assegno Unico Universale</h3>
        <p className="text-xs text-muted-foreground mb-4">Importi INPS 2026 con maggiorazioni per disabilità, figli piccoli, ecc.</p>
        <div className="space-y-4">
          <div>
            <Label>ISEE (opzionale — senza ISEE hai l'importo minimo)</Label>
            <Input type="number" value={isee} onChange={(e) => setIsee(e.target.value)} placeholder="Es. 22000" className="h-12 rounded-xl mt-1.5" data-testid="auu-isee" />
          </div>
          <div>
            <Label className="mb-2 block">Figli a carico</Label>
            {figli.map((f, i) => (
              <div key={f._key} className="flex gap-2 mb-2 items-end">
                <div className="flex-1">
                  <Label className="text-[10px] uppercase text-muted-foreground">Età</Label>
                  <Input type="number" value={f.eta} onChange={(e) => updateFiglio(i, "eta", e.target.value)} className="h-11 rounded-xl mt-1" min={0} max={24} data-testid={`auu-figlio-${i}-eta`} />
                </div>
                <div className="flex-[1.5]">
                  <Label className="text-[10px] uppercase text-muted-foreground">Disabilità</Label>
                  <Select value={f.disabilita || "no"} onValueChange={(v) => updateFiglio(i, "disabilita", v === "no" ? null : v)}>
                    <SelectTrigger className="h-11 rounded-xl mt-1" data-testid={`auu-figlio-${i}-disab`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">Nessuna</SelectItem>
                      <SelectItem value="media">Media</SelectItem>
                      <SelectItem value="grave">Grave</SelectItem>
                      <SelectItem value="non_autosufficiente">Non autosufficiente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeFiglio(i)} className="h-11 w-11 flex-shrink-0" disabled={figli.length === 1} data-testid={`auu-figlio-${i}-remove`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addFiglio} className="rounded-full mt-2" data-testid="auu-add-figlio">
              <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi figlio
            </Button>
          </div>
          <div className="space-y-3 pt-2 border-t">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Madre &lt; 21 anni</Label>
              <Switch checked={madreU21} onCheckedChange={setMadreU21} data-testid="auu-madre-u21" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Entrambi genitori lavoratori</Label>
              <Switch checked={entrambiLav} onCheckedChange={setEntrambiLav} data-testid="auu-entrambi-lav" />
            </div>
          </div>
          <Button onClick={submit} className="rounded-full w-full h-12" data-testid="auu-submit">Calcola AUU</Button>
        </div>
      </Card>
      {r && (
        <Card className="p-6 rounded-2xl border bg-accent/40" data-testid="auu-result">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">Assegno Unico stimato</div>
          <div className="text-3xl font-heading font-bold text-primary">{euro(r.totale_mensile)}<span className="text-sm text-muted-foreground font-normal">/mese</span></div>
          <div className="text-sm text-muted-foreground mb-1">{euro(r.totale_annuo)} all'anno</div>
          <div className="text-[11px] text-muted-foreground mb-4">{r.fascia_isee}</div>
          <div className="space-y-2 border-t pt-3">
            {r.figli.map((f) => (
              <div key={f.indice} className="text-sm">
                <div className="flex justify-between font-semibold">
                  <span>Figlio {f.indice} ({f.eta} anni{f.disabilita ? `, disab. ${f.disabilita}` : ""})</span>
                  <span className="font-mono">{euro(f.importo_mensile)}/m</span>
                </div>
                {f.maggiorazioni.length > 0 && (
                  <ul className="ml-3 text-xs text-muted-foreground mt-0.5">
                    {f.maggiorazioni.map((m, i) => <li key={m.nome}>+ {m.nome} ({euro(m.importo)})</li>)}
                  </ul>
                )}
              </div>
            ))}
            {r.maggiorazioni_nucleo?.length > 0 && (
              <div className="pt-2 border-t">
                <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">Maggiorazioni familiari</div>
                {r.maggiorazioni_nucleo.map((m) => (
                  <div key={m.nome} className="flex justify-between text-sm">
                    <span>{m.nome}</span><span className="font-mono">+ {euro(m.importo)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <ul className="mt-4 text-xs text-muted-foreground space-y-1 border-t pt-3">
            {r.note.map((n, i) => <li key={`n-${i}-${String(n).slice(0,30)}`}>• {n}</li>)}
          </ul>
        </Card>
      )}
    </div>
  );
}

function BonusMammaCalc({ user }) {
  const [nFigli, setNFigli] = useState(2);
  const [etaUltimo, setEtaUltimo] = useState(5);
  const [reddito, setReddito] = useState(user?.ral_lordo || 25000);
  const [r, setR] = useState(null);
  const submit = async () => {
    try {
      const res = await api.post("/calc/bonus-mamma", {
        n_figli: Number(nFigli), eta_ultimo_figlio: Number(etaUltimo), reddito_annuo: Number(reddito),
      });
      setR(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore calcolo"); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-6 rounded-2xl border">
        <h3 className="font-heading font-semibold text-xl mb-1">Bonus Mamma Lavoratrice</h3>
        <p className="text-xs text-muted-foreground mb-4">60€/mese (max 720€/anno) per lavoratrici con ≥2 figli e reddito ≤40.000€.</p>
        <div className="space-y-4">
          <div><Label>N° figli a carico</Label><Input type="number" min={0} value={nFigli} onChange={(e) => setNFigli(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="bm-n-figli" /></div>
          <div><Label>Età dell'ultimo figlio (anni)</Label><Input type="number" min={0} value={etaUltimo} onChange={(e) => setEtaUltimo(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="bm-eta" /></div>
          <div><Label>Reddito annuo lordo (€)</Label><Input type="number" value={reddito} onChange={(e) => setReddito(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="bm-reddito" /></div>
          <Button onClick={submit} className="rounded-full w-full h-12" data-testid="bm-submit">Verifica bonus</Button>
        </div>
      </Card>
      {r && (
        <Card className={`p-6 rounded-2xl border ${r.spetta ? "bg-primary/5 border-primary/20" : "bg-amber-50 border-amber-200"}`} data-testid="bm-result">
          <div className="flex items-start gap-3 mb-3">
            {r.spetta
              ? <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
              : <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />}
            <div>
              <div className="font-heading font-bold text-xl">
                {r.spetta ? `Ti spettano ${euro(r.importo_annuo)}` : "Bonus non spettante"}
              </div>
              <div className="text-sm text-muted-foreground">
                {r.spetta ? `${euro(r.importo_mensile)} × 12 mesi` : r.motivo}
              </div>
            </div>
          </div>
          {r.spetta && (
            <>
              <div className="text-sm mb-3"><strong>Erogazione:</strong> {r.erogazione}</div>
              <ul className="text-xs text-muted-foreground space-y-1 border-t pt-2">
                {r.requisiti_rispettati.map((req, i) => <li key={`req-${req}`}>✓ {req}</li>)}
              </ul>
              <p className="text-xs text-muted-foreground mt-3 border-t pt-2">💡 {r.come_richiedere}</p>
            </>
          )}
        </Card>
      )}
    </div>
  );
}

function BonusAsiloCalc({ user }) {
  const [isee, setIsee] = useState(user?.isee || 20000);
  const [r, setR] = useState(null);
  const submit = async () => {
    try {
      const res = await api.post("/calc/bonus-asilo", { isee: isee ? Number(isee) : null });
      setR(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore calcolo"); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-6 rounded-2xl border">
        <h3 className="font-heading font-semibold text-xl mb-1">Bonus Asilo Nido</h3>
        <p className="text-xs text-muted-foreground mb-4">Rimborso INPS spese asilo per figli &lt;3 anni. Fasce basate su ISEE.</p>
        <div className="space-y-4">
          <div><Label>ISEE (opzionale)</Label><Input type="number" value={isee} onChange={(e) => setIsee(e.target.value)} placeholder="Lascia vuoto per fascia minima" className="h-12 rounded-xl mt-1.5" data-testid="ba-isee" /></div>
          <Button onClick={submit} className="rounded-full w-full h-12" data-testid="ba-submit">Calcola bonus</Button>
        </div>
      </Card>
      {r && (
        <Card className="p-6 rounded-2xl border bg-accent/40" data-testid="ba-result">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">Rimborso stimato</div>
          <div className="text-3xl font-heading font-bold text-primary">{euro(r.importo_annuo)}<span className="text-sm text-muted-foreground font-normal">/anno</span></div>
          <div className="text-sm text-muted-foreground mb-2">Circa {euro(r.importo_mensile)}/mese su 11 mensilità</div>
          <div className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary inline-block mb-3">{r.fascia}</div>
          {Array.isArray(r.note) && (
            <ul className="mt-3 text-xs text-muted-foreground space-y-1 border-t pt-3">
              {r.note.map((n, i) => <li key={`n-${i}-${String(n).slice(0,30)}`}>• {n}</li>)}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}

function BonusRistrutturazioniCalc() {
  const [spesa, setSpesa] = useState(30000);
  const [r, setR] = useState(null);
  const submit = async () => {
    try { const res = await api.post("/calc/bonus-ristrutturazioni", { spesa: Number(spesa) }); setR(res.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-6 rounded-2xl border">
        <h3 className="font-heading font-semibold text-xl mb-1">Bonus Ristrutturazioni 50%</h3>
        <p className="text-xs text-muted-foreground mb-4">Detrazione IRPEF 50% su spese di ristrutturazione, ripartita in 10 anni. Tetto €96.000.</p>
        <div className="space-y-4">
          <div><Label>Spesa totale prevista (€)</Label><Input type="number" value={spesa} onChange={(e) => setSpesa(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="br-spesa" /></div>
          <Button onClick={submit} className="rounded-full w-full h-12" data-testid="br-submit">Calcola detrazione</Button>
        </div>
      </Card>
      {r && (
        <Card className="p-6 rounded-2xl border bg-accent/40" data-testid="br-result">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">Detrazione totale</div>
          <div className="text-3xl font-heading font-bold text-primary">{euro(r.detrazione_totale)}</div>
          <div className="text-sm text-muted-foreground mb-3">= {euro(r.quota_annua)} × {r.anni_ripartizione} anni</div>
          <div className="space-y-1.5 border-t pt-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Spesa inserita</span><span className="font-mono">{euro(r.spesa_inserita)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Spesa ammissibile ({r.percentuale}%)</span><span className="font-mono">{euro(r.spesa_ammissibile)}</span></div>
            {r.spesa_eccedente > 0 && <div className="flex justify-between text-amber-600"><span>⚠️ Spesa eccedente (non detrarrai)</span><span className="font-mono">{euro(r.spesa_eccedente)}</span></div>}
          </div>
          <ul className="mt-3 text-xs text-muted-foreground space-y-1 border-t pt-3">
            {r.note.map((n, i) => <li key={`n-${i}-${String(n).slice(0,30)}`}>• {n}</li>)}
          </ul>
        </Card>
      )}
    </div>
  );
}

function BonusMobiliCalc() {
  const [spesa, setSpesa] = useState(3000);
  const [haRistrut, setHaRistrut] = useState(true);
  const [r, setR] = useState(null);
  const submit = async () => {
    try { const res = await api.post("/calc/bonus-mobili", { spesa: Number(spesa), ha_ristrutturazione: haRistrut }); setR(res.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-6 rounded-2xl border">
        <h3 className="font-heading font-semibold text-xl mb-1">Bonus Mobili 50%</h3>
        <p className="text-xs text-muted-foreground mb-4">Detrazione 50% su mobili/elettrodomestici classe A. Tetto €5.000. Serve ristrutturazione collegata.</p>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50">
            <Label className="text-sm">Ho una ristrutturazione in corso?</Label>
            <Switch checked={haRistrut} onCheckedChange={setHaRistrut} data-testid="bmob-ristrut" />
          </div>
          <div><Label>Spesa mobili (€)</Label><Input type="number" value={spesa} onChange={(e) => setSpesa(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="bmob-spesa" /></div>
          <Button onClick={submit} className="rounded-full w-full h-12" data-testid="bmob-submit">Calcola detrazione</Button>
        </div>
      </Card>
      {r && (
        <Card className={`p-6 rounded-2xl border ${r.spetta === false ? "bg-amber-50 border-amber-200" : "bg-accent/40"}`} data-testid="bmob-result">
          {r.spetta === false ? (
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-heading font-bold text-xl">Bonus non applicabile</div>
                <div className="text-sm text-muted-foreground">{r.motivo}</div>
              </div>
            </div>
          ) : (
            <>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">Detrazione totale</div>
              <div className="text-3xl font-heading font-bold text-primary">{euro(r.detrazione_totale)}</div>
              <div className="text-sm text-muted-foreground mb-3">= {euro(r.quota_annua)} × {r.anni_ripartizione} anni</div>
              <div className="space-y-1.5 border-t pt-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Spesa ammissibile</span><span className="font-mono">{euro(r.spesa_ammissibile)}</span></div>
                {r.spesa_eccedente > 0 && <div className="flex justify-between text-amber-600"><span>⚠️ Spesa oltre tetto</span><span className="font-mono">{euro(r.spesa_eccedente)}</span></div>}
              </div>
              <ul className="mt-3 text-xs text-muted-foreground space-y-1 border-t pt-3">
                {r.note.map((n, i) => <li key={`n-${i}-${String(n).slice(0,30)}`}>• {n}</li>)}
              </ul>
            </>
          )}
        </Card>
      )}
    </div>
  );
}

function BonusEnergiaCalc({ user }) {
  const [isee, setIsee] = useState(user?.isee || 8000);
  const [nfigli, setNfigli] = useState(user?.figli_a_carico?.length || 0);
  const [disagio, setDisagio] = useState(false);
  const [r, setR] = useState(null);
  const submit = async () => {
    try {
      const res = await api.post("/calc/bonus-energia", {
        isee: isee ? Number(isee) : null, numero_figli: Number(nfigli), disagio_fisico: disagio,
      });
      setR(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-6 rounded-2xl border">
        <h3 className="font-heading font-semibold text-xl mb-1">Bonus Sociale Energia</h3>
        <p className="text-xs text-muted-foreground mb-4">Sconto AUTOMATICO in bolletta luce e gas per famiglie con ISEE basso (dal 2021 non serve più domanda).</p>
        <div className="space-y-4">
          <div><Label>ISEE (€)</Label><Input type="number" value={isee} onChange={(e) => setIsee(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="be-isee" /></div>
          <div><Label>N° figli nel nucleo</Label><Input type="number" min={0} value={nfigli} onChange={(e) => setNfigli(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="be-figli" hint="≥4 figli alza la soglia ISEE a 20.000 €" /></div>
          <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50">
            <Label className="text-sm">Disagio fisico (apparecchi elettromedicali)</Label>
            <Switch checked={disagio} onCheckedChange={setDisagio} data-testid="be-disagio" />
          </div>
          <Button onClick={submit} className="rounded-full w-full h-12" data-testid="be-submit">Verifica bonus</Button>
        </div>
      </Card>
      {r && (
        <Card className={`p-6 rounded-2xl border ${r.spetta === false ? "bg-amber-50 border-amber-200" : "bg-accent/40"}`} data-testid="be-result">
          {r.spetta === false ? (
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-heading font-bold text-xl">Non spetta</div>
                <div className="text-sm text-muted-foreground">{r.motivo}</div>
              </div>
            </div>
          ) : (
            <>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">Sconto stimato</div>
              <div className="text-3xl font-heading font-bold text-primary">{euro(r.totale_annuo)}<span className="text-sm text-muted-foreground font-normal">/anno</span></div>
              <div className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary inline-block mb-3 mt-1">{r.fascia}</div>
              <div className="space-y-1.5 border-t pt-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Sconto elettrico</span><span className="font-mono">{euro(r.sconto_elettrico_annuo)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Sconto gas</span><span className="font-mono">{euro(r.sconto_gas_annuo)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Componenti nucleo (stima)</span><span className="font-mono">{r.componenti_nucleo_stimati}</span></div>
              </div>
              <ul className="mt-3 text-xs text-muted-foreground space-y-1 border-t pt-3">
                {r.note.map((n, i) => <li key={`n-${i}-${String(n).slice(0,30)}`}>• {n}</li>)}
              </ul>
            </>
          )}
        </Card>
      )}
    </div>
  );
}

export default function Calculators() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  // Deep-link via ?tab= — es. /app/calcolatori?tab=irpef (dal countdown scadenze)
  const tabParam = searchParams.get("tab") || "";
  // Tab set per la sezione P.IVA/generica
  const pivaTabs = new Set(["irpef", "forfettario", "iva", "isee", "bollo"]);
  // Tab set per la sezione dipendente/famiglia
  const dipendenteTabs = new Set(["busta-paga", "auu", "bonus-mamma", "bonus-asilo", "bonus-ristrut", "bonus-mobili", "bonus-energia"]);
  // Utente puro P.IVA (no dipendente) → nasconde i tab dipendenti se preferisce
  const isDipendenteRelevant =
    (user?.tipo_piva === "nessuna") || user?.has_dipendente_income;
  const isPivaRelevant = user?.tipo_piva === "forfettario" || user?.tipo_piva === "ordinario";
  const dipendenteDefault = dipendenteTabs.has(tabParam) ? tabParam : "busta-paga";
  const pivaDefault = pivaTabs.has(tabParam) ? tabParam : "irpef";
  // Auto-scroll alla sezione target quando arriva via deep-link
  useEffect(() => {
    if (!tabParam) return;
    const isDep = dipendenteTabs.has(tabParam);
    const targetId = isDep ? "sec-dipendente" : "sec-piva";
    const el = document.getElementById(targetId);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  }, [tabParam]);
  return (
    <div className="space-y-6" data-testid="calculators-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2">Calcolatori fiscali</div>
        <h1 className="font-heading font-bold text-4xl tracking-tighter">Calcoli, senza calcolatrice.</h1>
        <p className="text-muted-foreground mt-2">Parametri 2026 aggiornati (Legge Bilancio 2025 · Circolare INPS n.7/2026).</p>
      </div>

      {/* Sezione DIPENDENTI / FAMIGLIA */}
      {isDipendenteRelevant && (
        <div id="sec-dipendente">
          <div className="flex items-center gap-2 mt-8 mb-3">
            <div className="w-1 h-6 rounded bg-primary" />
            <h2 className="font-heading font-bold text-xl">Lavoro dipendente e famiglia</h2>
          </div>
          <Tabs defaultValue={dipendenteDefault}>
            <TabsList className="rounded-full h-11 bg-muted p-1 flex-wrap">
              <TabsTrigger value="busta-paga" className="rounded-full px-4" data-testid="tab-busta-paga">Busta Paga</TabsTrigger>
              <TabsTrigger value="auu" className="rounded-full px-4" data-testid="tab-auu">Assegno Unico</TabsTrigger>
              <TabsTrigger value="bonus-mamma" className="rounded-full px-4" data-testid="tab-bonus-mamma">Bonus Mamma</TabsTrigger>
              <TabsTrigger value="bonus-asilo" className="rounded-full px-4" data-testid="tab-bonus-asilo">Bonus Asilo</TabsTrigger>
              <TabsTrigger value="bonus-ristrut" className="rounded-full px-4" data-testid="tab-bonus-ristrut">Ristrutturazioni</TabsTrigger>
              <TabsTrigger value="bonus-mobili" className="rounded-full px-4" data-testid="tab-bonus-mobili">Bonus Mobili</TabsTrigger>
              <TabsTrigger value="bonus-energia" className="rounded-full px-4" data-testid="tab-bonus-energia">Bonus Energia</TabsTrigger>
            </TabsList>
            <TabsContent value="busta-paga" className="mt-6"><BustaPagaCalc user={user} /></TabsContent>
            <TabsContent value="auu" className="mt-6"><AssegnoUnicoCalc user={user} /></TabsContent>
            <TabsContent value="bonus-mamma" className="mt-6"><BonusMammaCalc user={user} /></TabsContent>
            <TabsContent value="bonus-asilo" className="mt-6"><BonusAsiloCalc user={user} /></TabsContent>
            <TabsContent value="bonus-ristrut" className="mt-6"><BonusRistrutturazioniCalc /></TabsContent>
            <TabsContent value="bonus-mobili" className="mt-6"><BonusMobiliCalc /></TabsContent>
            <TabsContent value="bonus-energia" className="mt-6"><BonusEnergiaCalc user={user} /></TabsContent>
          </Tabs>
        </div>
      )}

      {/* Sezione FISCO / P.IVA */}
      <div id="sec-piva" className="flex items-center gap-2 mt-8 mb-3">
        <div className="w-1 h-6 rounded bg-secondary" />
        <h2 className="font-heading font-bold text-xl">{isPivaRelevant ? "Fisco e P.IVA" : "Calcolatori generici"}</h2>
      </div>
      <Tabs defaultValue={pivaDefault}>
        <TabsList className="rounded-full h-11 bg-muted p-1 flex-wrap">
          <TabsTrigger value="irpef" className="rounded-full px-4" data-testid="tab-irpef">IRPEF</TabsTrigger>
          {isPivaRelevant && <TabsTrigger value="forfettario" className="rounded-full px-4" data-testid="tab-forfettario">Forfettario</TabsTrigger>}
          {isPivaRelevant && <TabsTrigger value="iva" className="rounded-full px-4" data-testid="tab-iva">IVA</TabsTrigger>}
          <TabsTrigger value="isee" className="rounded-full px-4" data-testid="tab-isee">ISEE</TabsTrigger>
          <TabsTrigger value="bollo" className="rounded-full px-4" data-testid="tab-bollo">Bollo</TabsTrigger>
        </TabsList>
        <TabsContent value="irpef" className="mt-6"><IrpefCalc /></TabsContent>
        {isPivaRelevant && <TabsContent value="forfettario" className="mt-6"><ForfettarioCalc /></TabsContent>}
        {isPivaRelevant && <TabsContent value="iva" className="mt-6"><IvaCalc /></TabsContent>}
        <TabsContent value="isee" className="mt-6"><IseeCalc /></TabsContent>
        <TabsContent value="bollo" className="mt-6"><BolloCalc /></TabsContent>
      </Tabs>
    </div>
  );
}
